import React from "react";
import {
  Divider,
  Grid,
  Typography,
  Button,
  TextField,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";

import PageConfig, { PageContext } from "../../config";
import PageDomain from "../../domain";

const PROMO_CODE_VIEW = Object.freeze({
  ADD_PROMO_CODE_TEXT: "ADD_PROMO_CODE_TEXT",
  PROMO_CODE_FORM: "PROMO_CODE_FORM",
  APPLIED_PROMO_CODE: "APPLIED_PROMO_CODE",
});

// Component AddPromoCodeForm
const AddPromoCodeForm = ({ setPromoCodeView }) => {
  const { t } = useTranslation();
  const {
    promoCode,
    setPromoCode,
    setShouldApplyPromoCode,
    formErrors,
    setFormErrors,
  } = React.useContext(PageContext);

  const onClickCancel = () => {
    setPromoCode("");
    setFormErrors({ ...formErrors, promoCode: "" });
    setShouldApplyPromoCode(false);
    setPromoCodeView(PROMO_CODE_VIEW.ADD_PROMO_CODE_TEXT);
  };

  const onClickApply = () => {
    if (isEmpty(promoCode)) {
      setFormErrors({
        promoCode: t("errors.formRequiredField", {
          fieldName: t("payment.promoCode"),
        }),
      });
      return;
    }
    setShouldApplyPromoCode(true);
  };

  return (
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Typography className="typography-body-2">
        {t("payment.promoCode")}
      </Typography>
      <Grid
        xs
        item
        container
        direction="column"
        justifyContent="flex-end"
        alignItems="flex-end"
        className="row-gap-8"
      >
        <Grid item xs={8} container direction="column" className="row-gap-4">
          <TextField
            size="small"
            variant="outlined"
            value={promoCode}
            onChange={(event) => {
              setPromoCode(event.target.value);
            }}
            inputProps={{ autoFocus: true }}
          />
          {!isEmpty(formErrors.promoCode) && (
            <Typography className="typography-caption-1 form-error-text-accent-1">
              {formErrors.promoCode}
            </Typography>
          )}
        </Grid>
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          className="column-gap-8"
        >
          <Button
            size="small"
            className="secondary-button"
            onClick={onClickApply}
          >
            {t("payment.apply")}
          </Button>
          <Button
            size="small"
            className="secondary-button"
            variant="contained"
            onClick={onClickCancel}
          >
            {t("common.cancel")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

// Component: PromoCodeView
const PromoCodeView = ({
  promoCodeView,
  setPromoCodeView,
  calculatedInvoice,
}) => {
  const { t } = useTranslation();

  const onClickAddPromoCodeText = () => {
    setPromoCodeView(PROMO_CODE_VIEW.PROMO_CODE_FORM);
  };

  return (
    <>
      {promoCodeView === PROMO_CODE_VIEW.PROMO_CODE_FORM && (
        <AddPromoCodeForm setPromoCodeView={setPromoCodeView} />
      )}
      {promoCodeView === PROMO_CODE_VIEW.ADD_PROMO_CODE_TEXT && (
        <Typography
          className="typography-caption-1 add-promo-code-txt"
          onClick={onClickAddPromoCodeText}
        >
          <i class="fa fa-plus margin-right-4" aria-hidden="true"></i>
          {t("payment.addPromoCode")}
        </Typography>
      )}
      {promoCodeView === PROMO_CODE_VIEW.APPLIED_PROMO_CODE && (
        <Grid container direction="row" justifyContent="space-between">
          <Typography className="typography-body-2 capitalized-text text-green-500">
            {t("payment.promotionalDiscount", {
              percentage: calculatedInvoice.discountPercentage,
            })}
          </Typography>
          <Typography className="typography-body-2 capitalized-text text-green-500">
            {`- ${calculatedInvoice.discountAmount}`}
          </Typography>
        </Grid>
      )}
    </>
  );
};

// Component: BillRecepientInfomation
const BillRecepientInfomation = React.memo(() => {
  const { paymentLinkData } = React.useContext(PageContext);
  const post = paymentLinkData.post ?? {};

  const name = post.personalInfo?.displayName ?? "";
  const email = post.email ?? "";
  const phone = post.phone ?? "";

  return (
    <Grid container direction="column">
      {name && <Typography className="typography-body-2">{name}</Typography>}
      {email && <Typography className="typography-body-2">{email}</Typography>}
      {phone && <Typography className="typography-body-2">{phone}</Typography>}
    </Grid>
  );
});

// Component: InvoicePricingView
const InvoicePricingView = () => {
  const { t } = useTranslation();
  const { ADD_PROMO_CODE_ACTIVATED } = PageConfig;
  const {
    paymentLinkData,
    promoCode,
    shouldApplyPromoCode,
    setFormErrors,
    setShouldApplyPromoCode,
  } = React.useContext(PageContext);
  const [simulatedCheckoutTier, setSimulatedCheckoutTier] = React.useState({});
  const [promoCodeView, setPromoCodeView] = React.useState(
    PROMO_CODE_VIEW.ADD_PROMO_CODE_TEXT
  );

  const calculatedInvoice = React.useMemo(
    () =>
      PageDomain.calculateInvoiceObject({
        checkoutTier: simulatedCheckoutTier,
        paymentLinkData,
      }),
    [paymentLinkData, simulatedCheckoutTier]
  );

  React.useEffect(() => {
    if (
      isEmpty(paymentLinkData) ||
      !shouldApplyPromoCode ||
      isEmpty(promoCode)
    ) {
      return;
    }
    PageDomain.simulateCheckoutTierWithPromoCode({
      paymentLinkData,
      promoCode,
      setFormErrors,
      setSimulatedCheckoutTier,
      setPromoCodeView,
      setShouldApplyPromoCode,
      t,
    });
  }, [
    paymentLinkData,
    promoCode,
    setFormErrors,
    setShouldApplyPromoCode,
    shouldApplyPromoCode,
    t,
  ]);

  React.useEffect(() => {
    if (isEmpty(paymentLinkData)) {
      return;
    }
    PageDomain.simulateCheckoutTier({
      paymentLinkData,
      setSimulatedCheckoutTier,
    });
  }, [paymentLinkData]);

  const label1 = paymentLinkData.isRenew
    ? t("payment.renewalFee")
    : t("payment.advertiingFee");
  const pricingTier = paymentLinkData.pricingTier ?? {};
  const pricingTierEnum = PageConfig.PRICING_TIERS_ENUM[pricingTier.id];

  return (
    <Grid container direction="column" className="row-gap-8">
      <Grid container direction="row" justifyContent="space-between">
        <Typography className="typography-body-2 capitalized-text">
          {t("common.package")}
        </Typography>
        <Typography className="typography-body-2">
          {pricingTierEnum.label}
        </Typography>
      </Grid>
      {promoCodeView === PROMO_CODE_VIEW.APPLIED_PROMO_CODE && (
        <Grid container direction="row" justifyContent="space-between">
          <Typography className="typography-body-2 capitalized-text text-green-500">
            {t("payment.promoCode")}
          </Typography>
          <Typography className="typography-body-2 text-green-500">
            {promoCode}
          </Typography>
        </Grid>
      )}
      <Divider />
      <Grid container direction="row" justifyContent="space-between">
        <Typography className="typography-body-2 capitalized-text">
          {label1}
        </Typography>
        <Typography className="typography-body-2 capitalized-text">
          {calculatedInvoice.packageFee}
        </Typography>
      </Grid>
      {ADD_PROMO_CODE_ACTIVATED && (
        <PromoCodeView
          promoCodeView={promoCodeView}
          setPromoCodeView={setPromoCodeView}
          calculatedInvoice={calculatedInvoice}
        />
      )}
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        className="total-due-row"
      >
        <Typography className="typography-body-2 text-semi-bold capitalized-text">
          {t("payment.totalDue")}
        </Typography>
        <Typography className="typography-body-2 text-semi-bold">
          {calculatedInvoice.totalDue}
        </Typography>
      </Grid>
    </Grid>
  );
};

// Component: InvoiceView
const InvoiceView = () => {
  const { t } = useTranslation();

  return (
    <Grid container direction="column" className="invoice-view row-gap-16">
      <Typography className="typography-body-1 capitalized-text text-semi-bold">
        {t("common.invoice")}
      </Typography>
      <BillRecepientInfomation />
      <Divider />
      <InvoicePricingView />
    </Grid>
  );
};

export default InvoiceView;
